import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter, HashRouter } from "react-router-dom";
import App from "./App";
import GlobalStateProvider from "@/GlobalStateProvider";
import { RememberLastRoute } from "@/components/RememberLastRoute";
import { PublicClientApplication, EventType } from "@azure/msal-browser";
import { MsalProvider } from "@azure/msal-react";
import { msalConfig } from "@/auth/msalConfig";
import { setMsalInstance } from "@/auth/msalInstance";

// Import fetch interceptor voor 403 error handling
import "@/helpers/fetchInterceptor";

const root = createRoot(document.getElementById("root")!);

const isDev = import.meta.env.DEV;
const Router = isDev ? HashRouter : BrowserRouter;

const msalInstance = new PublicClientApplication(msalConfig);
setMsalInstance(msalInstance);

// Zet activeAccount bij succesvolle login
msalInstance.addEventCallback((event) => {
    if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
        const payload = event.payload as any;
        if (payload.account) msalInstance.setActiveAccount(payload.account);
    }
});

function cleanHashForRouter(hash: string) {
    // behoud alleen '#/pad...'
    const idxQ = hash.indexOf("?");
    const idxA = hash.indexOf("&");
    const cut = [idxQ, idxA].filter(i => i >= 0).sort((a, b) => a - b)[0];
    return cut !== undefined ? hash.slice(0, cut) : hash;
}

async function bootstrap() {
    const hash = window.location.hash;
    const hasOAuthParams = hash.includes('code=') && hash.includes('state=');

    if (!hasOAuthParams && (hash.includes("error=") || hash.includes("#/"))) {
        if (!hash.startsWith("#/")) {
            window.history.replaceState({}, document.title, window.location.origin + window.location.pathname + "#/");
        }
    }

    await msalInstance.initialize();

    const result = await msalInstance.handleRedirectPromise();

    if (result?.account) {
        msalInstance.setActiveAccount(result.account);

        const savedHash = sessionStorage.getItem("postLoginHash") || "#/";
        sessionStorage.removeItem("postLoginHash");
        window.location.hash = savedHash;
    } else if (hasOAuthParams) {
        window.history.replaceState({}, document.title, window.location.origin + "/#/");
    }

    const accounts = msalInstance.getAllAccounts();

    if (accounts.length > 0 && !msalInstance.getActiveAccount()) {
        msalInstance.setActiveAccount(accounts[0]);
    }

    root.render(
        <StrictMode>
            <MsalProvider instance={msalInstance}>
                <Router basename="/">
                    <RememberLastRoute />
                    <GlobalStateProvider>
                        <App />
                    </GlobalStateProvider>
                </Router>
            </MsalProvider>
        </StrictMode>
    );
}

bootstrap();
